/**
 * Tiled Window System Types
 * 
 * A scalable windowing system for displaying entity viewers (workflows, agents, tools, etc.)
 * in draggable, resizable tiled windows. Supports recursive drilling - clicking a workflow
 * in a window opens a new window showing that workflow's graph.
 */

import type { Workflow, Agent, Tool, Prompt, Skill, Role, Policy, Schema } from './admin';
import type { AslDefinition } from './workflow-graph';
import type { AvailableResources } from './agent-graph';
import type { ModelWithBackup, AgentReference } from './model-graph';

/** Supported entity types that can be displayed in tiled windows */
export type TiledWindowEntityType = 
    | 'workflow'
    | 'agent'
    | 'tool'
    | 'prompt'
    | 'model'
    | 'skill'
    | 'role'
    | 'policy'
    | 'schema'
    | 'decision';

/** Position and size of a tiled window */
export interface WindowGeometry {
    x: number;
    y: number;
    width: number;
    height: number;
}

/** State of a single tiled window */
export type WindowState = 'normal' | 'minimized' | 'maximized';

/** Base window data structure */
export interface TiledWindowBase {
    id: string;
    title: string;
    entityType: TiledWindowEntityType;
    entityId: string | number;
    entityName: string;
    /** Application key for this entity (used for nested drill-down lookups) */
    appKey?: string;
    geometry: WindowGeometry;
    state: WindowState;
    zIndex: number;
    isLoading?: boolean;
    error?: string | null;
    /** Parent window ID for tracking drill-down hierarchy */
    parentWindowId?: string;
}

/** Window containing a workflow graph */
export interface WorkflowWindow extends TiledWindowBase {
    entityType: 'workflow';
    data: {
        workflow: Workflow;
        definition: AslDefinition;
        availableWorkflows: Array<{
            id: string;
            key: string;
            name: string;
            definition: AslDefinition;
        }>;
        agents: Array<{ name: string; fqcn: string; description?: string }>;
        tools: Array<{ name: string; fqcn: string; description?: string }>;
        models: Array<{ name: string; fqcn: string; description?: string }>;
        /** For replay mode: ID of the workflow run to display */
        replayRunId?: string;
    };
}

/** Window containing an agent graph */
export interface AgentWindow extends TiledWindowBase {
    entityType: 'agent';
    data: {
        agent: Agent;
        resources: AvailableResources;
    };
}

/** Window containing a tool viewer */
export interface ToolWindow extends TiledWindowBase {
    entityType: 'tool';
    data: {
        tool: Tool;
    };
}

/** Window containing a prompt viewer */
export interface PromptWindow extends TiledWindowBase {
    entityType: 'prompt';
    data: {
        prompt: Prompt;
        domainTypes?: {
            categories: string[];
            byCategory: Record<string, Array<{
                key: string;
                label: string;
                description?: string;
                category: string;
            }>>;
        };
    };
}

/** Window containing a model graph viewer */
export interface ModelWindow extends TiledWindowBase {
    entityType: 'model';
    data: {
        model: ModelWithBackup;
        agents?: AgentReference[];
    };
}

/** Window containing a skill viewer */
export interface SkillWindow extends TiledWindowBase {
    entityType: 'skill';
    data: {
        skill: Skill;
    };
}

/** Window containing a role viewer */
export interface RoleWindow extends TiledWindowBase {
    entityType: 'role';
    data: {
        role: Role;
    };
}

/** Window containing a policy viewer */
export interface PolicyWindow extends TiledWindowBase {
    entityType: 'policy';
    data: {
        policy: Policy;
    };
}

/** Window containing a schema viewer */
export interface SchemaWindow extends TiledWindowBase {
    entityType: 'schema';
    data: {
        schema: Schema;
    };
}

/** Window containing a decision viewer */
export interface DecisionWindow extends TiledWindowBase {
    entityType: 'decision';
    data: Record<string, unknown>;
}

/** Union of all window types */
export type TiledWindow = 
    | WorkflowWindow 
    | AgentWindow 
    | ToolWindow 
    | PromptWindow 
    | ModelWindow
    | SkillWindow
    | RoleWindow
    | PolicyWindow
    | SchemaWindow
    | DecisionWindow;

/** Request to open a new tiled window */
export interface OpenWindowRequest {
    entityType: TiledWindowEntityType;
    entityId: string | number;
    entityName: string;
    /** Application key for capability API lookup (e.g. "conversation") */
    appKey?: string;
    /** Parent window ID for drill-down tracking */
    parentWindowId?: string;
    /** Initial position (defaults to cascade from last window) */
    position?: { x: number; y: number };
    /** Initial size (defaults based on entity type) */
    size?: { width: number; height: number };
    /** For workflow windows in replay mode: ID of the workflow run to display */
    replayRunId?: string;
}

/** Context value for the TiledWindow system */
export interface TiledWindowContextValue {
    /** All open windows */
    windows: TiledWindow[];
    /** ID of the currently focused window */
    focusedWindowId: string | null;
    /** Open a new window */
    openWindow: (request: OpenWindowRequest) => Promise<string>;
    /** Close a window by ID */
    closeWindow: (id: string) => void;
    /** Close all windows */
    closeAllWindows: () => void;
    /** Focus a window (bring to front) */
    focusWindow: (id: string) => void;
    /** Update window geometry */
    updateGeometry: (id: string, geometry: Partial<WindowGeometry>) => void;
    /** Update window state (normal/minimized/maximized) */
    updateState: (id: string, state: WindowState) => void;
    /** Get a window by ID */
    getWindow: (id: string) => TiledWindow | undefined;
    /** Check if a window for an entity is already open */
    findWindowByEntity: (entityType: TiledWindowEntityType, entityId: string | number) => TiledWindow | undefined;
}

/** Default window sizes by entity type */
export const DEFAULT_WINDOW_SIZES: Record<TiledWindowEntityType, { width: number; height: number }> = {
    workflow: { width: 900, height: 650 },
    agent: { width: 850, height: 600 },
    tool: { width: 600, height: 500 },
    prompt: { width: 700, height: 550 },
    model: { width: 800, height: 550 },  // Larger for ModelGraph visualization
    skill: { width: 600, height: 450 },
    role: { width: 600, height: 450 },
    policy: { width: 700, height: 550 },
    schema: { width: 700, height: 550 },
    decision: { width: 850, height: 650 },
};

/** Minimum window dimensions */
export const MIN_WINDOW_SIZE = { width: 400, height: 300 };

/** Cascade offset for new windows */
export const CASCADE_OFFSET = { x: 30, y: 30 };

/** Labels for entity types */
export const ENTITY_TYPE_LABELS: Record<TiledWindowEntityType, string> = {
    workflow: 'Workflow',
    agent: 'Agent',
    tool: 'Tool',
    prompt: 'Prompt',
    model: 'Model',
    skill: 'Skill',
    role: 'Role',
    policy: 'Policy',
    schema: 'Schema',
    decision: 'Decision',
};

